import { Divider } from '@hivebrite/design-system/Divider';

import ImageArrangement from 'hb-builder/components/BuilderComponents/block/Hero/Settings/Content/BackgroundImage/ImageArrangement';
import CheckboxSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/CheckboxSetting';
import ColorSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/ColorSetting';
import DeleteImageSource from 'hb-builder/components/common/SettingsLibrary/CommonSettings/DeleteImageSource';
import EditImageSource from 'hb-builder/components/common/SettingsLibrary/CommonSettings/EditImageSource';
import NumberSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/NumberSetting';
import Padding from 'hb-builder/components/common/SettingsLibrary/CommonSettings/Padding';
import SectionHeight from 'hb-builder/components/common/SettingsLibrary/CommonSettings/SectionHeight';
import TextSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/TextSetting';
import AccordionTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/AccordionTitle';
import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import { useBuilderConfigContext } from 'hb-builder/hooks';
import type { ComponentSettingsTab } from 'hb-builder/types/settings';
import { isPaddingTooBig } from 'hb-builder/utils/attributes';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';

const Section = ({
  defaultAttributes,
  item,
  onChange,
  permissions: {
    can: {
      alertOnCustomPadding,
      canHaveRoundedCorner,
      imageBg: canSetImageBackground,
    },
  } = { can: {} },
}: ComponentSettingsTab<SectionItemAttributes>) => {
  const {
    features: { color },
    hivebrite: { networkColors, networkTheming },
  } = useBuilderConfigContext();
  const colorSettingsProps = {
    color,
    networkColors,
    networkTheming,
  };

  const settingProps = { defaultAttributes, item, onChange };
  const enableRoundedCorners =
    canHaveRoundedCorner && !item?.attributes?.fullWidth;
  const hasImage = Boolean(item?.attributes?.containerBackgroundImage);

  return (
    <AccordionTitle isInitiallyOpen title={__('Section')}>
      <InputTitle
        boxProps={{ mb: 3 }}
        id={`containerBackgroundColor-bg-${item.id}`}
        label={__('Background')}
      />
      <ColorSetting
        settingKey="containerBackgroundColor"
        title={__('Color background')}
        transparent
        {...settingProps}
        {...colorSettingsProps}
      />
      {canSetImageBackground && (
        <>
          <InputTitle
            boxProps={{ mb: 3 }}
            id={`containerBackgroundColor-bg-image-${item.id}`}
            label={__('Image background')}
          />
          <EditImageSource hasImage={hasImage} id={item.id} />
          {hasImage && (
            <DeleteImageSource
              settingKey="attributes.containerBackgroundImage"
              {...settingProps}
            />
          )}
          <ImageArrangement {...settingProps} />
          <TextSetting
            settingKey="attributes.containerBackgroundImageAlt"
            title={__('Alternative text')}
            description={s__(
              "Builder|This will appear if the image can't be displayed",
            )}
            {...settingProps}
          />
          <Divider mt={0} />
        </>
      )}
      <Padding
        alert={{
          enabled: alertOnCustomPadding,
          fillConditions: (padding) => isPaddingTooBig(padding, 30),
          message: s__(
            'Builder|Please check if this value displays well on mobile',
          ),
        }}
        settingKey="containerPadding"
        title={__('Padding (in pixels)')}
        {...settingProps}
      />
      <CheckboxSetting
        settingKey="fullWidth"
        title={__('Full width background')}
        {...settingProps}
      />
      {enableRoundedCorners && (
        <NumberSetting
          settingKey="cornerRadius"
          title={__('Rounded corner')}
          {...settingProps}
        />
      )}
      <SectionHeight {...settingProps} />
    </AccordionTitle>
  );
};

export default Section;
